"use client"

import { useMemo } from "react"
import { PieChart, Pie, Cell, Label } from "recharts"
import { IconChartPie } from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"

import type { LeadStats } from "@/types"

interface LeadsStatusChartProps {
  data: LeadStats | null
  isLoading: boolean
}

export function LeadsStatusChart({ data, isLoading }: LeadsStatusChartProps) {
  const t = useTranslations("dashboard")
  const chartData = useMemo(() => {
    if (!data?.by_status) return []
    return data.by_status.map((status) => ({
      name: status.name,
      value: status.count,
      fill: status.color,
    }))
  }, [data])

  const chartConfig = useMemo(() => {
    if (!data?.by_status) return {} as ChartConfig
    return data.by_status.reduce((acc, status) => {
      acc[status.name] = {
        label: status.name,
        color: status.color,
      }
      return acc
    }, {} as ChartConfig)
  }, [data])

  const totalLeads = data?.total ?? 0

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("charts.leadsStatus.title")}>
        <div className="flex items-center justify-center h-[200px]">
          <Skeleton className="h-[160px] w-[160px] rounded-full" />
        </div>
        <div className="flex justify-center gap-3 mt-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Skeleton className="h-2.5 w-2.5 rounded-full" />
              <Skeleton className="h-3 w-12" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  if (!data || chartData.length === 0) {
    return (
      <ApolloPanel eyebrow={t("charts.leadsStatus.title")}>
        <div className="flex flex-col items-center justify-center h-[200px] text-center">
          <div className="rounded-full bg-muted p-3 mb-3">
            <IconChartPie className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("charts.leadsStatus.empty.title")}
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {t("charts.leadsStatus.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel eyebrow={t("charts.leadsStatus.title")}>
      <ChartContainer
        config={chartConfig}
        className="mx-auto aspect-square h-[200px]"
      >
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel />}
          />
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            innerRadius={50}
            outerRadius={80}
            strokeWidth={2}
            stroke="hsl(var(--background))"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy}
                        className="fill-foreground font-serif text-[26px] leading-none tracking-[-0.01em] tabular-nums"
                      >
                        {totalLeads.toLocaleString()}
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + 20}
                        className="fill-muted-foreground text-xs"
                      >
                        {t("charts.leadsStatus.totalLabel")}
                      </tspan>
                    </text>
                  )
                }
              }}
            />
          </Pie>
        </PieChart>
      </ChartContainer>
      <div className="flex flex-wrap justify-center gap-3 mt-2">
        {chartData.map((status) => (
          <div key={status.name} className="flex items-center gap-1.5 text-xs">
            <div
              className="h-2.5 w-2.5 rounded-full shrink-0"
              style={{ backgroundColor: status.fill }}
            />
            <span className="text-muted-foreground">
              {status.name}
            </span>
            <span className="font-medium">{status.value}</span>
          </div>
        ))}
      </div>
    </ApolloPanel>
  )
}
